import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../common/Icon/Icon';
import { adminFetch, ago, dateTime, clock } from './adminUtil';

/**
 * The server's own error log (helpers/errorLog.js), grouped: the same
 * error a hundred times is one row with a count. Pick one to see where
 * it happened and the code path. "Mark all read" clears the overview's
 * reminder; the log itself stays.
 */
function AdminErrors({ onChanged }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);
    const [picked, setPicked] = useState(null);

    const load = useCallback(async () => {
        try { setD(await adminFetch('/api/admin/errors')); setErr(null); }
        catch (e) { setErr(e.message); }
    }, []);
    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const markRead = async () => {
        try {
            await adminFetch('/api/admin/errors/seen', { method: 'POST' });
            await load();
            onChanged();
        } catch (e) { setErr(e.message); }
    };

    if (err && !d) return <div className="adm-banner bad">{err}</div>;
    if (!d) return <p className="adm-loading">Reading the error log&hellip;</p>;

    const group = d.groups.find(g => g.id === picked) || d.groups[0];
    const restarts = d.starts.slice(0, 8);

    return (
        <div className="adm-errors">
            <div className="adm-errors-head">
                <p>
                    {d.groups.length === 0
                        ? <>No errors recorded on <strong>{d.host}</strong>.</>
                        : <><strong>{d.groups.length}</strong> kind{d.groups.length === 1 ? '' : 's'} of error on {d.host}
                            {d.newCount > 0 && <>, <strong className="adm-new">{d.newCount} new</strong></>}.</>}
                </p>
                {d.newCount > 0 && (
                    <button type="button" className="adm-btn" onClick={markRead}>
                        <Icon name="check" size={13} /> Mark all read
                    </button>
                )}
            </div>

            {restarts.length > 0 && (
                <p className="adm-starts">
                    <Icon name="refresh" size={13} />
                    Server started {restarts.map((t, i) => (
                        <span key={t} title={dateTime(t)}>{i > 0 && ', '}{ago(t)}</span>
                    ))}
                    {d.crashes24h > 0 && <strong className="adm-new"> · {d.crashes24h} crash{d.crashes24h === 1 ? '' : 'es'} today</strong>}
                </p>
            )}

            {d.groups.length === 0 ? (
                <div className="adm-empty">
                    <Icon name="check-circle" size={28} />
                    Quiet. Anything the server logs as an error lands here, grouped.
                </div>
            ) : (
                <div className="adm-split two errors">
                    <ul className="adm-err-list">
                        {d.groups.map(g => (
                            <li key={g.id}>
                                <button
                                    type="button"
                                    className={`adm-err${g === group ? ' active' : ''}${g.kind === 'crash' ? ' crash' : ''}`}
                                    onClick={() => setPicked(g.id)}
                                >
                                    <span className="adm-err-count">{g.count > 999 ? '999+' : g.count}</span>
                                    <span className="adm-err-main">
                                        <span className="adm-err-msg">{g.kind === 'crash' && 'CRASH: '}{g.message}</span>
                                        <small>
                                            {g.isNew && <em className="adm-new">new</em>}
                                            last {ago(g.last)}{g.where[0] ? ` · ${g.where[0]}` : ''}
                                        </small>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {group && (
                        <article className="adm-record adm-err-detail">
                            <h3>{group.message}</h3>
                            <dl className="adm-facts">
                                <div><dt>Times</dt><dd>{group.count}</dd></div>
                                <div><dt>First</dt><dd>{dateTime(group.first)}</dd></div>
                                <div><dt>Latest</dt><dd>{dateTime(group.last)} ({clock(group.last)})</dd></div>
                                <div><dt>Where</dt><dd>{group.where.length ? group.where.join(', ') : 'outside a request (startup or a timer)'}</dd></div>
                            </dl>
                            <div className="adm-console static">
                                <div className="adm-console-bar"><Icon name="terminal" size={14} /><span>Code path, newest occurrence</span></div>
                                <pre>{group.stack || 'No stack trace: this was logged as a plain message.'}</pre>
                            </div>
                        </article>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminErrors;
